import { getBaseEventGroups, type BaseEventGroup } from './base-events';

/**
 * The events the mParticle connector can forward, for the
 * `/integrations/mparticle_out` config page.
 *
 * A strict subset of the base catalog — narrower than the data-out allowlist in
 * `data-out-events.ts`, which covers everything. mParticle takes no
 * `drafted`/`attempted`/`deferred`/`dropped` lifecycle events, no
 * `whatsapp`/`slack`/`webhook` channels, and no `*_unsubscribed` beyond the
 * customer-level pair.
 *
 * Kept explicit for the same reason as the data-out list: adding a channel to
 * the base catalog must not silently start offering its events here, because a
 * connector that cannot forward them would accept the subscription and drop the
 * traffic. `mparticle-events.test.ts` fails when a name here stops existing in
 * the base catalog, which is the prompt to decide.
 */
const MPARTICLE_EVENT_NAMES: ReadonlySet<string> = new Set([
  'customer_subscribed',
  'customer_unsubscribed',
  'email_sent',
  'email_delivered',
  'email_opened',
  'email_clicked',
  'email_converted',
  'email_bounced',
  'email_spammed',
  'email_failed',
  'in_app_sent',
  'in_app_opened',
  'in_app_clicked',
  'in_app_converted',
  'in_app_failed',
  'in_app_undeliverable',
  'push_sent',
  'push_delivered',
  'push_opened',
  'push_clicked',
  'push_converted',
  'push_bounced',
  'push_failed',
  'sms_sent',
  'sms_delivered',
  'sms_clicked',
  'sms_converted',
  'sms_bounced',
  'sms_failed',
]);

export { MPARTICLE_EVENT_NAMES };

/**
 * The base catalog narrowed to the mParticle allowlist: groups with no supported
 * event drop out entirely, and surviving groups keep only supported items.
 * Group and item order follow the base catalog.
 */
export function getMParticleEventGroups(): readonly Readonly<BaseEventGroup>[] {
  return Object.freeze(
    getBaseEventGroups()
      .map((group) => ({ ...group, items: group.items.filter((item) => MPARTICLE_EVENT_NAMES.has(item.value)) }))
      .filter((group) => group.items.length > 0),
  );
}

export default getMParticleEventGroups();
